import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search, MapPin, Phone, Globe, Shield, Save,
  ArrowRight, CheckCircle2, Building2, Filter,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import API_BASE from '../../utils/apiBase';
import SalesOutreachCompliance from '../../components/portal/SalesOutreachCompliance';
import StaffingPipelineGuide from '../../components/portal/StaffingPipelineGuide';

const API = API_BASE;

const RATING_COLORS = {
  Outstanding: 'bg-emerald-100 text-emerald-800',
  Good: 'bg-green-100 text-green-800',
  'Requires improvement': 'bg-amber-100 text-amber-800',
  Inadequate: 'bg-red-100 text-red-800',
  'Not rated': 'bg-gray-100 text-gray-600',
};

const DEFAULT_REGIONS = [
  'North East', 'North West', 'Yorkshire & Humberside', 'East Midlands',
  'West Midlands', 'East of England', 'London', 'South East', 'South West',
];

export default function LeadDiscoveryPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [filterOptions, setFilterOptions] = useState(null);
  const [name, setName] = useState('');
  const [postcode, setPostcode] = useState('');
  const [region, setRegion] = useState('South East');
  const [localAuthority, setLocalAuthority] = useState('');
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [searched, setSearched] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [searchHint, setSearchHint] = useState('');
  const [browseMode, setBrowseMode] = useState(false);
  const [autoLoadAttempted, setAutoLoadAttempted] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      fetch(`${API}/lead-discovery/status`, { headers }).then((r) => (r.ok ? r.json() : null)),
      fetch(`${API}/lead-discovery/filters`, { headers }).then((r) => (r.ok ? r.json() : null)),
    ])
      .then(([statusData, filtersData]) => {
        setStatus(statusData);
        setFilterOptions(filtersData);
        if (filtersData?.default_region) {
          setRegion(filtersData.default_region);
        }
      })
      .catch(() => {});
  }, []);

  const runSearch = useCallback(async (pageNum = 1, opts = {}) => {
    const searchName = opts.name !== undefined ? opts.name : name;
    const searchPostcode = opts.postcode !== undefined ? opts.postcode : postcode;
    const searchRegion = opts.region !== undefined ? opts.region : region;
    const searchLa = opts.localAuthority !== undefined ? opts.localAuthority : localAuthority;

    const hasFilter = searchName.trim() || searchPostcode.trim() || searchLa.trim();
    if (!searchRegion.trim() && !hasFilter) {
      toast.error('Choose a region or enter a postcode');
      return;
    }

    setLoading(true);
    setSearchError('');
    setSearchHint('');
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ page: String(pageNum), per_page: '20' });
      if (searchName.trim()) params.set('name', searchName.trim());
      if (searchPostcode.trim()) params.set('postcode', searchPostcode.trim());
      if (searchRegion.trim()) params.set('region', searchRegion.trim());
      if (searchLa.trim()) params.set('local_authority', searchLa.trim());

      const res = await fetch(`${API}/lead-discovery/search?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setResults(data.locations || []);
        setTotal(data.total || 0);
        setTotalPages(data.total_pages || 1);
        setPage(pageNum);
        setBrowseMode(Boolean(data.browse_mode));
        setSearchHint(data.message || '');
        setSearched(true);
      } else {
        const err = await res.json().catch(() => ({}));
        const msg = typeof err.detail === 'object' ? err.detail?.message : err.detail;
        const friendly = msg || 'Search failed — check CQC API key on Railway backend';
        setSearchError(friendly);
        setResults([]);
        setTotal(0);
        setSearched(true);
        toast.error(friendly);
      }
    } catch {
      setSearchError('Search failed — check your connection');
      setSearched(true);
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  }, [name, postcode, region, localAuthority]);

  useEffect(() => {
    if (status?.cqc_api_configured && !autoLoadAttempted && !loading) {
      setAutoLoadAttempted(true);
      runSearch(1, { name: '', postcode: '', localAuthority: '' });
    }
  }, [status?.cqc_api_configured, autoLoadAttempted, loading, runSearch]);

  const saveLead = async (loc) => {
    setSavingId(loc.cqc_location_id);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/lead-discovery/save-lead`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cqc_location_id: loc.cqc_location_id,
          company_name: loc.company_name,
          organisation_type: loc.organisation_type,
          contact_phone: loc.contact_phone,
          website_url: loc.website_url,
          address_line_1: loc.address_line_1,
          city: loc.city,
          postcode: loc.postcode,
          cqc_rating: loc.cqc_rating,
          cqc_provider_id: loc.cqc_provider_id,
          bed_count: loc.bed_count,
          local_authority: loc.local_authority,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.already_exists) {
          toast.info(`Already in CRM: ${data.sales_lead.lead_code}`);
          navigate(`/portal/sales-leads?lead=${data.sales_lead.id}`);
        } else {
          toast.success(`Saved ${data.sales_lead.lead_code} — opening Sales CRM`);
          navigate(`/portal/sales-leads?lead=${data.sales_lead.id}`);
        }
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save lead');
      }
    } catch {
      toast.error('Failed to save lead');
    } finally {
      setSavingId(null);
    }
  };

  const clearFilters = () => {
    setName('');
    setPostcode('');
    setLocalAuthority('');
    setRegion(filterOptions?.default_region || 'South East');
    runSearch(1, { name: '', postcode: '', localAuthority: '', region: filterOptions?.default_region || 'South East' });
  };

  const regions = filterOptions?.regions || DEFAULT_REGIONS;
  const councils = filterOptions?.kent_local_authorities || [];
  const apiReady = status?.cqc_api_configured;
  const hasActiveFilter = name.trim() || postcode.trim() || localAuthority.trim();

  const resultsSummary = () => {
    if (!searched) return '';
    const parts = [];
    if (region) parts.push(region);
    if (localAuthority) parts.push(localAuthority);
    if (postcode.trim()) parts.push(`postcode ${postcode.trim().toUpperCase()}`);
    if (name.trim()) parts.push(`"${name.trim()}"`);
    if (parts.length === 0) return 'all regions';
    return parts.join(' · ');
  };

  return (
    <div className="space-y-6" data-testid="lead-discovery-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Find care home leads</h1>
          <p className="text-sm text-text-muted mt-1 max-w-2xl">
            Search the official CQC register, save prospects to Sales CRM, then contact and convert to clients.
          </p>
        </div>
        <Button variant="outline" asChild>
          <Link to="/portal/sales-leads">Open Sales CRM <ArrowRight className="h-4 w-4 ml-1" /></Link>
        </Button>
      </div>

      <StaffingPipelineGuide currentStep={1} />

      {apiReady ? (
        <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
          CQC register connected — care homes load automatically. Narrow by postcode or council, then save to CRM.
        </p>
      ) : (
        <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 space-y-2">
          <p className="font-semibold">Add your CQC API key to Railway</p>
          <p>
            Paste your <strong>Primary key</strong> into Railway → backend → Variables as{' '}
            <code className="bg-amber-100 px-1 rounded">CQC_API_KEY</code>, redeploy, then refresh.
          </p>
          <p className="text-xs">
            Until then,{' '}
            <Link to="/portal/sales-leads?create=1" className="font-medium underline">add leads manually in Sales CRM</Link>.
          </p>
        </div>
      )}

      {searchError && (
        <p className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          {searchError}
        </p>
      )}

      <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50 flex items-center gap-2">
          <Filter className="h-4 w-4 text-primary" />
          <p className="text-sm font-semibold">Search filters</p>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Region</Label>
              <Select value={region} onValueChange={setRegion}>
                <SelectTrigger>
                  <SelectValue placeholder="Select region" />
                </SelectTrigger>
                <SelectContent>
                  {regions.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Council area (optional)</Label>
              <Select value={localAuthority || '__all__'} onValueChange={(v) => setLocalAuthority(v === '__all__' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="All councils" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All councils in region</SelectItem>
                  {councils.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Postcode area</Label>
              <Input
                placeholder="e.g. ME14, TN23"
                value={postcode}
                onChange={(e) => setPostcode(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && runSearch(1)}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Care home name</Label>
              <Input
                placeholder="e.g. Meadow, Sunrise"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && runSearch(1)}
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => runSearch(1)} disabled={loading || !apiReady}>
              <Search className="h-4 w-4 mr-1" />
              {loading ? 'Searching…' : 'Search'}
            </Button>
            {hasActiveFilter && (
              <Button variant="outline" onClick={clearFilters} disabled={loading}>
                Clear filters
              </Button>
            )}
          </div>
        </div>
      </div>

      {(searched || loading) && (
        <div className="rounded-xl border bg-white overflow-hidden shadow-sm">
          <div className="px-4 py-3 border-b bg-gray-50 flex flex-wrap justify-between items-center gap-2">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary" />
              <p className="text-sm font-medium">
                {loading ? 'Loading care homes…' : (
                  <>
                    {total.toLocaleString()} care home{total === 1 ? '' : 's'}
                    {browseMode && !hasActiveFilter ? ' in register' : ` — ${resultsSummary()}`}
                  </>
                )}
              </p>
            </div>
            {!loading && totalPages > 1 && (
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => runSearch(page - 1)} disabled={page <= 1}>
                  Previous
                </Button>
                <span className="text-xs text-text-muted self-center px-2">
                  Page {page} of {totalPages}
                </span>
                <Button size="sm" variant="outline" onClick={() => runSearch(page + 1)} disabled={page >= totalPages}>
                  Next
                </Button>
              </div>
            )}
          </div>

          {loading && results.length === 0 ? (
            <div className="p-12 text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              <p className="text-sm text-text-muted mt-3">Fetching from CQC register…</p>
            </div>
          ) : results.length === 0 ? (
            <div className="p-8 text-center space-y-2">
              <p className="text-sm text-text-muted">No care homes matched your filters.</p>
              {searchHint && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 inline-block">
                  {searchHint}
                </p>
              )}
              <p className="text-xs text-text-muted">
                Try <strong>Clear filters</strong> to browse {region}, or enter postcode <strong>ME7</strong> / <strong>ME14</strong>.
              </p>
              <Button size="sm" variant="outline" onClick={clearFilters} className="mt-2">
                Show all {region} care homes
              </Button>
            </div>
          ) : (
            <div className="divide-y">
              {results.map((loc) => (
                <div key={loc.cqc_location_id || loc.company_name} className="p-4 flex flex-wrap gap-4 justify-between hover:bg-gray-50/80">
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold text-sm">{loc.company_name}</p>
                      {loc.cqc_rating && (
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${RATING_COLORS[loc.cqc_rating] || RATING_COLORS['Not rated']}`}>
                          {loc.cqc_rating}
                        </span>
                      )}
                      {loc.already_in_crm && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">
                          In CRM {loc.lead_code || ''}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-text-muted mt-1 flex items-center gap-1">
                      <MapPin className="h-3 w-3 flex-shrink-0" />
                      {[loc.address_line_1, loc.city, loc.postcode].filter(Boolean).join(', ') || 'Address on CQC profile'}
                    </p>
                    <div className="flex flex-wrap gap-3 mt-2 text-xs">
                      {loc.contact_phone && (
                        <span className="flex items-center gap-1 text-text-muted">
                          <Phone className="h-3 w-3" />{loc.contact_phone}
                        </span>
                      )}
                      {loc.website_url && (
                        <a
                          href={loc.website_url.startsWith('http') ? loc.website_url : `https://${loc.website_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-primary hover:underline"
                        >
                          <Globe className="h-3 w-3" />Website
                        </a>
                      )}
                      {loc.cqc_profile_url && (
                        <a href={loc.cqc_profile_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-primary hover:underline">
                          <Shield className="h-3 w-3" />CQC profile
                        </a>
                      )}
                      {loc.bed_count != null && <span className="text-text-muted">{loc.bed_count} beds</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {loc.already_in_crm && loc.sales_lead_id ? (
                      <Button size="sm" variant="outline" asChild>
                        <Link to={`/portal/sales-leads?lead=${loc.sales_lead_id}`}>Open in CRM</Link>
                      </Button>
                    ) : (
                      <Button size="sm" onClick={() => saveLead(loc)} disabled={savingId === loc.cqc_location_id}>
                        <Save className="h-3 w-3 mr-1" />
                        {savingId === loc.cqc_location_id ? 'Saving…' : 'Save to CRM'}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <SalesOutreachCompliance />
    </div>
  );
}